import styled, { createGlobalStyle, css, keyframes } from 'styled-components';

import { ANIM, ART_STYLE, ART_STYLE_STACK, COLORS, MOBILE, REDUCED_MOTION, SPACE } from '../../theme/tokens';
import { ART_MORPH, MORPH_ROOT_CLASS } from './useIngredientsMorph';

/** Full-bleed content column under the Website tabs, matching the Logos tab beside it. */
export const Page = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${SPACE[16]};

  ${MOBILE} {
    gap: ${SPACE[12]};
  }
`;

/** The styles strip's stand-in while the brand's list loads. */
export const StripSkeleton = styled.div`
  display: flex;
  gap: ${SPACE[12]};
  overflow: hidden;

  ${MOBILE} {
    gap: ${SPACE[8]};
  }
`;

/** Ingredients and samples share one container: ingredients left, samples right, a hairline between them;
 *  stacked (ingredients first, the hairline turning horizontal) when narrow. Before the first extraction there are no
 *  samples to show: the ingredients are in their sheet and the card holds the DraftPlaceholder instead. */
export const Studio = styled.div`
  display: grid;
  grid-template-columns: minmax(280px, ${ART_STYLE.RAIL_PX}px) 1px minmax(0, 1fr);
  justify-content: center;
  gap: ${SPACE[24]};
  /* Stretch, so the ingredients column runs the container's full height and its sticky footer sits at the foot. */
  align-items: stretch;

  ${ART_STYLE_STACK} {
    grid-template-columns: minmax(0, 1fr);
    gap: ${SPACE[20]};
  }
`;

/* The style's name and its action as the card's header: edge to edge (the negative margins undo the Panel's
 * 24px / 26px padding, 20px / 16px on mobile, off the scale on purpose), a shade lighter than the card. */
export const CardHeader = styled.div`
  margin: -24px -26px ${SPACE[24]};
  padding: ${SPACE[20]} 26px;
  border-radius: 20px 20px 0 0;
  border-bottom: 1px solid ${COLORS.hairline};
  background: ${COLORS.surfaceFainter};

  ${MOBILE} {
    margin: -20px -16px ${SPACE[20]};
    padding: ${SPACE[16]} 16px;
  }
`;

export const StudioColumn = styled.section`
  min-width: 0;
`;

/** The ingredients column: a flex column, so ExtractBar (its sticky footer) settles at the bottom. */
export const IngredientsColumn = styled(StudioColumn)`
  display: flex;
  flex-direction: column;
`;

export const StudioDivider = styled.div`
  align-self: stretch;
  background: ${COLORS.hairline};

  ${ART_STYLE_STACK} {
    height: 1px;
  }
`;

const enter = keyframes`
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: none;
  }
`;

/** A section's soft entrance once the style has loaded (fade + slight rise), staggered by `$index`. */
export const Enter = styled.div<{ $index: number }>`
  min-width: 0;
  ${({ $index }) => css`
    animation: ${enter} ${ART_STYLE.SECTION_ENTER_MS}ms ${ANIM.EASE} both;
    animation-delay: ${$index * ART_STYLE.SECTION_STAGGER_MS}ms;
  `}

  ${REDUCED_MOTION} {
    animation: none;
  }
`;

/** Loading stand-in blocks, laid out like the real page. */
export const SkeletonStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[12]};
`;

export const Failure = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${SPACE[8]};
`;

/* ── The sheet-to-rail morph (useIngredientsMorph) ───────────────────────────
 * A view transition: the browser snapshots the sheet, the page commits the split, and these pseudo-elements animate
 * between the two. Everything here is scoped to MORPH_ROOT_CLASS, which is on <html> only while the morph runs. */

const morphFadeOut = keyframes`
  to {
    opacity: 0;
  }
`;

const morphFadeIn = keyframes`
  from {
    opacity: 0;
  }
`;

const morphSamplesSlide = keyframes`
  from {
    opacity: 0;
    transform: translateX(${ART_STYLE.SPLIT_SLIDE_PX}px);
  }
`;

const morphSamplesRise = keyframes`
  from {
    opacity: 0;
    transform: translateY(${ART_STYLE.SPLIT_SLIDE_PX / 2}px);
  }
`;

/** The morph's transition names and its animations. Mounted with the page; inert until the morph puts its class on. */
export const MorphStyles = createGlobalStyle`
  /* The sheet's card (the dialog holding the Ingredients body) becomes the rail's column. */
  html.${MORPH_ROOT_CLASS} [data-art-morph='${ART_MORPH.card}'],
  html.${MORPH_ROOT_CLASS} [role='dialog']:has([data-art-morph='${ART_MORPH.body}']) {
    view-transition-name: art-morph-card;
  }

  html.${MORPH_ROOT_CLASS} [data-art-morph='${ART_MORPH.head}'] {
    view-transition-name: art-morph-head;
  }

  html.${MORPH_ROOT_CLASS} [data-art-morph='${ART_MORPH.body}'] {
    view-transition-name: art-morph-body;
  }

  html.${MORPH_ROOT_CLASS} [data-art-morph='${ART_MORPH.samples}'] {
    view-transition-name: art-morph-samples;
  }

  html.${MORPH_ROOT_CLASS}::view-transition-group(art-morph-card),
  html.${MORPH_ROOT_CLASS}::view-transition-group(art-morph-head),
  html.${MORPH_ROOT_CLASS}::view-transition-group(art-morph-body) {
    animation-duration: ${ART_STYLE.MORPH_MS}ms;
    animation-timing-function: ${ART_STYLE.SPLIT_EASE};
  }

  /* The heading and the body glide at their own size, never stretched: laid out at the same width in the sheet and
     the rail, the two snapshots match and the cross-fade between them does not show (a narrow studio's wider rail
     cross-fades into place). */
  html.${MORPH_ROOT_CLASS}::view-transition-old(art-morph-head),
  html.${MORPH_ROOT_CLASS}::view-transition-new(art-morph-head),
  html.${MORPH_ROOT_CLASS}::view-transition-old(art-morph-body),
  html.${MORPH_ROOT_CLASS}::view-transition-new(art-morph-body) {
    width: auto;
    height: auto;
  }

  /* The sheet's card shrinks into the rail's box, stretched over it, and dissolves on the way. */
  html.${MORPH_ROOT_CLASS}::view-transition-old(art-morph-card) {
    height: 100%;
    animation: ${morphFadeOut} ${ART_STYLE.MORPH_CARD_FADE_MS}ms ${ART_STYLE.SPLIT_EASE} both;
  }

  html.${MORPH_ROOT_CLASS}::view-transition-new(art-morph-card) {
    height: 100%;
    animation: ${morphFadeIn} ${ART_STYLE.MORPH_CARD_FADE_MS}ms ${ART_STYLE.SPLIT_EASE} both;
  }

  /* The rest of the page: the dim lifts and the studio's card grows, at the morph's pace. */
  html.${MORPH_ROOT_CLASS}::view-transition-old(root),
  html.${MORPH_ROOT_CLASS}::view-transition-new(root) {
    animation-duration: ${ART_STYLE.MORPH_DIM_MS}ms;
    animation-timing-function: ${ART_STYLE.SPLIT_EASE};
  }

  /* The samples slide in beside the rail a beat behind (rise in below it when stacked), as the split's arrival does. */
  html.${MORPH_ROOT_CLASS}::view-transition-new(art-morph-samples) {
    animation: ${morphSamplesSlide} ${ART_STYLE.SPLIT_SAMPLES_MS}ms ${ART_STYLE.SPLIT_EASE}
      ${ART_STYLE.SPLIT_SAMPLES_DELAY_MS}ms both;
  }

  ${ART_STYLE_STACK} {
    html.${MORPH_ROOT_CLASS}::view-transition-new(art-morph-samples) {
      animation-name: ${morphSamplesRise};
    }
  }
`;
